import Hub from "../hub"
import Model from "../model/model"
import { ID } from "../model/types"
import { ViewBounds } from "../plate_viewer"

class IndexedDBOperations {
  private dbName: string
  private storeName: string

  constructor(dbName: string, storeName: string) {
    this.dbName = dbName
    this.storeName = storeName
  }

  private async openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 2)

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        db.createObjectStore(this.storeName)
      }

      request.onsuccess = () => {
        resolve(request.result)
      }

      request.onerror = () => {
        reject(request.error)
      }
    })
  }

  public async saveState(
    value: ID | null | { x: number; y: number; scale: number } | boolean,
    key: string
  ): Promise<void> {
    const db = await this.openDB()
    const transaction = db.transaction(this.storeName, "readwrite")
    const objectStore = transaction.objectStore(this.storeName)
    objectStore.put(value, key)
  }

  public async getState(id: string): Promise<any | null> {
    return new Promise(async (resolve, reject) => {
      const db = await this.openDB()
      const transaction = db.transaction(this.storeName, "readonly")
      const objectStore = transaction.objectStore(this.storeName)
      const request = objectStore.get(id)

      request.onsuccess = () => {
        resolve(request.result)
      }

      request.onerror = () => {
        reject(request.error)
      }
    })
  }
}

class Local {
  model: Model
  db: IndexedDBOperations

  constructor() {
    this.db = new IndexedDBOperations("nana", "main")
  }

  attachToHub(hub: Hub) {
    hub.on("idea-pane:open", ({ ideaId }) => {
      this.db.saveState(ideaId, "open-idea")
    })

    hub.on("idea-pane:close", () => {
      this.db.saveState(null, "open-idea")
    })
  }

  async saveViewBounds({ x, y, scale }: ViewBounds): Promise<void> {
    this.db.saveState({ x, y, scale }, "view-bounds")
  }

  async saveLockState(locked: boolean): Promise<void> {
    this.db.saveState(locked, "lock-state")
  }

  async getOpenIdeaId(): Promise<ID | null> {
    const id = await this.db.getState("open-idea")

    // If nothing in the db, then null (clients use undefined to mean not checked yet)
    if (id === undefined) return null

    // If the idea has been deleted.
    if (this.model.getIdea(id) === null) return null

    return id
  }

  async getViewBounds(): Promise<ViewBounds | null> {
    const viewBounds = await this.db.getState("view-bounds")

    // If nothing in the db, then null (clients use undefined to mean not checked yet)
    if (viewBounds === undefined) return null
    return viewBounds
  }

  async getLockState(): Promise<boolean | null> {
    const lockState = await this.db.getState("lock-state")

    // If nothing in the db, then null (clients use undefined to mean not checked yet)
    if (lockState === undefined) return null
    return lockState
  }
}

export default Local
